/**
 * openFelixWithPrompt — the publisher half of the "open Felix coaching"
 * bridge (HOME-01, Claire findings 2/5: no dead interactive elements on
 * the home surface; every item deep-links or opens Felix coaching).
 *
 * MECHANISM: surfaced items that have no deep-linkable route (org-wide
 * blocker buckets, briefing suggestion chips, nudges) publish the
 * `felix:open-prompt` window event carrying the coaching prompt.
 * FelixChat.tsx is the single subscriber — it opens the panel and seeds
 * its composer through the existing suggestion-chip auto-send mechanism
 * (`pendingSuggestionRef` + `setInput`), so the prompt fires immediately
 * instead of sitting dead in the composer.
 *
 * Mirrors the established FelixChat window-event idioms:
 *   - "felix:open-qa-session"            (QASessions.tsx → FelixChat.tsx)
 *   - "ftl.felix.sidebar.open_category"  (FelixChat.sidebarOpenCategory.ts)
 */

export const FELIX_OPEN_PROMPT_EVENT = "felix:open-prompt";

export interface FelixOpenPromptDetail {
  /** The coaching prompt to seed Felix's composer with. */
  prompt: string;
  /** Emitting surface, for debugging (e.g. "attention_panel:my_blockers"). */
  source?: string;
  /** Default true — FelixChat auto-sends the prompt via its
   *  suggestion-chip flow. Pass false to only pre-fill the composer. */
  autoSend?: boolean;
}

/**
 * Open the Felix chat panel seeded with `prompt`.
 * Returns false (and dispatches nothing) for an empty prompt.
 */
export function openFelixWithPrompt(
  prompt: string,
  opts: { source?: string; autoSend?: boolean } = {},
): boolean {
  if (!prompt.trim() || typeof window === "undefined") return false;
  window.dispatchEvent(
    new CustomEvent<FelixOpenPromptDetail>(FELIX_OPEN_PROMPT_EVENT, {
      detail: { prompt, source: opts.source, autoSend: opts.autoSend },
    }),
  );
  return true;
}

/**
 * Shared action router for briefing suggestion / required-action values,
 * which arrive as EITHER a route ("/projects/…"), an absolute url, or a
 * plain-language chip text:
 *   - internal path  → SPA navigate
 *   - http(s) url    → new tab (noopener)
 *   - anything else  → Felix's composer, pre-filled and NOT sent: the
 *                      person sends it (a click is not a question; row 5ef53b44)
 *
 * Used by BriefingFirstLanding + V2 Home so their BriefingCards act
 * identically (HOME-01: required-action clicks must never no-op).
 */
export function followSuggestionOrCoach(
  value: string,
  navigate: (to: string) => void,
  source: string,
): void {
  const v = value.trim();
  if (!v) return;
  if (v.startsWith("/")) {
    navigate(v);
    return;
  }
  if (/^https?:\/\//i.test(v)) {
    window.open(v, "_blank", "noopener,noreferrer");
    return;
  }
  openFelixWithPrompt(v, { source, autoSend: false });
}
